/**
 * Accordion Custom - Self-contained (no @theme/utilities dependency)
 * Timing aligned with lulu-accordion.js for consistent feel.
 */

const _mqLarge = matchMedia('(min-width: 750px)');
function _isMobile() { return !_mqLarge.matches; }

class AccordionCustom extends HTMLElement {
  get details() {
    const d = this.querySelector('details');
    if (!(d instanceof HTMLDetailsElement)) throw new Error('Details not found');
    return d;
  }

  get summary() {
    const s = this.details.querySelector('summary');
    if (!(s instanceof HTMLElement)) throw new Error('Summary not found');
    return s;
  }

  get content() {
    return this.details.querySelector('.details-content');
  }

  get #disableOnMobile() { return this.dataset.disableOnMobile === 'true'; }
  get #disableOnDesktop() { return this.dataset.disableOnDesktop === 'true'; }
  get #closeWithEscape() { return this.dataset.closeWithEscape === 'true'; }

  #controller = new AbortController();
  #animation = null;
  #isClosing = false;
  #isExpanding = false;

  get #isHeaderMenu() {
    return Boolean(this.closest('header-drawer, .menu-drawer, .menu-drawer__menu'));
  }

  connectedCallback() {
    // Don't override header/mobile-menu accordion behavior — header-drawer.js owns those
    if (this.#isHeaderMenu) return;

    const { signal } = this.#controller;
    this.#setDefaultOpenState();
    this.addEventListener('keydown', this.#handleKeyDown.bind(this), { signal });
    this.summary.addEventListener('click', this.#handleClick.bind(this), { signal });
    _mqLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #handleClick(event) {
    const isMobile = _isMobile();
    if ((isMobile && this.#disableOnMobile) || (!isMobile && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    if (this.#isClosing || this.#isExpanding) return;
    this.details.open ? this.#close() : this.#open();
  }

  #open() {
    if (_isMobile()) this.#closeSiblings();

    this.classList.add('is-open');
    this.details.style.overflow = 'hidden';
    this.details.open = true;
    this.#isExpanding = true;

    const startHeight = `${this.summary.offsetHeight}px`;
    const contentHeight = this.content ? this.content.offsetHeight : 0;
    const endHeight = `${this.summary.offsetHeight + contentHeight}px`;

    if (this.#animation) this.#animation.cancel();

    this.#animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 200, easing: 'ease-out' }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(true);
    this.#animation.oncancel = () => { this.#isExpanding = false; };
  }

  #close() {
    this.#isClosing = true;
    this.classList.remove('is-open');
    this.details.style.overflow = 'hidden';

    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    if (this.#animation) this.#animation.cancel();

    this.#animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 150, easing: 'ease-out' }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(false);
    this.#animation.oncancel = () => { this.#isClosing = false; };
  }

  #onAnimationFinish(open) {
    this.details.open = open;
    this.#animation = null;
    this.#isClosing = false;
    this.#isExpanding = false;
    this.details.style.height = '';
    this.details.style.overflow = '';
  }

  #closeSiblings() {
    const parent = this.closest('.accordion, .product-details, [data-accordion-group], .lulu-accordion-group, .footer__grid');
    if (!parent) return;
    parent.querySelectorAll('accordion-custom').forEach((acc) => {
      if (acc !== this && acc.details?.open) acc.closeAccordion();
    });
  }

  closeAccordion() {
    if (this.details.open && !this.#isClosing) this.#close();
  }

  #handleMediaQueryChange = () => { this.#setDefaultOpenState(); };

  #setDefaultOpenState() {
    const isMobile = _isMobile();
    const shouldBeOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    this.details.open = shouldBeOpen;
    this.classList.toggle('is-open', shouldBeOpen);
  }

  #handleKeyDown(event) {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
